import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Users,
  Upload,
  Plus,
  Search,
  Trash2,
  Edit,
  Filter,
  Check,
  Loader2,
  Download,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Modal, ModalFooter } from '@/components/ui/modal';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { useI18n } from '@/i18n';
import { validateEmail } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import type { UserRole } from '@/types';
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  batchDeleteUsers,
  importUsers,
  exportUsersUrl,
  type UserData,
  type ListUsersParams,
} from '@/api/users';
import {
  getGroups,
  createGroup,
  deleteGroup,
  type GroupData,
} from '@/api/groups';

export default function UsersPage() {
  const { t } = useI18n();
  const { toast } = useToast();

  // Data states
  const [users, setUsers] = useState<UserData[]>([]);
  const [groups, setGroups] = useState<GroupData[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserData | null>(null);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [groupFilter, setGroupFilter] = useState<string>('all');

  // Form states
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'user' as UserRole,
    groups: [] as string[],
  });
  const [importError, setImportError] = useState<string>('');
  const [newGroupName, setNewGroupName] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params: ListUsersParams = {
        page,
        pageSize,
      };
      if (searchQuery) params.search = searchQuery;
      if (roleFilter !== 'all') params.role = roleFilter;
      if (groupFilter !== 'all') params.group = groupFilter;

      const result = await getUsers(params);
      setUsers(result.items);
      setTotal(result.total);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load users';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery, roleFilter, groupFilter, toast, t.common.error]);

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    try {
      const result = await getGroups();
      setGroups(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load groups';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    }
  }, [toast, t.common.error]);

  // Initial fetch
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, roleFilter, groupFilter]);

  // Handle select all
  const handleSelectAll = () => {
    if (selectedIds.size === users.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(users.map((u) => u.id)));
    }
  };

  // Handle single select
  const handleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (!confirm(t.confirm.deleteUsers.replace('{count}', String(selectedIds.size)))) {
      return;
    }

    try {
      setSubmitting(true);
      await batchDeleteUsers(Array.from(selectedIds));
      setSelectedIds(new Set());
      toast({
        title: t.common.success,
        description: t.admin.usersDeleted,
      });
      fetchUsers();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete users';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle add/edit user
  const handleSubmit = async () => {
    if (!validateEmail(formData.email)) {
      toast({
        title: t.common.error,
        description: t.admin.invalidEmail,
        variant: 'destructive',
      });
      return;
    }

    if (!editingUser && !formData.password) {
      toast({
        title: t.common.error,
        description: t.admin.passwordRequired,
        variant: 'destructive',
      });
      return;
    }

    try {
      setSubmitting(true);
      if (editingUser) {
        await updateUser(editingUser.id, {
          name: formData.name,
          role: formData.role,
          groups: formData.groups,
          ...(formData.password ? { password: formData.password } : {}),
        });
        toast({
          title: t.common.success,
          description: t.admin.userUpdated,
        });
      } else {
        await createUser({
          email: formData.email,
          name: formData.name,
          password: formData.password,
          role: formData.role,
          groups: formData.groups,
        });
        toast({
          title: t.common.success,
          description: t.admin.userCreated,
        });
      }
      closeModal();
      fetchUsers();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save user';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete user
  const handleDeleteUser = async (id: string) => {
    if (!confirm(t.confirm.deleteUser)) {
      return;
    }

    try {
      setSubmitting(true);
      await deleteUser(id);
      toast({
        title: t.common.success,
        description: t.admin.userDeleted,
      });
      fetchUsers();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete user';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle CSV import
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setSubmitting(true);
      const result = await importUsers(file);

      if (result.failed > 0) {
        const errorMessages = result.errors.slice(0, 5).map(
          (err) => `Row ${err.row}: ${err.error} (${err.email})`
        ).join('\n');
        setImportError(
          `${t.admin.importSuccess.replace('{count}', String(result.success))}, ` +
          `${t.admin.importFailed.replace('{count}', String(result.failed))}:\n${errorMessages}`
        );
      } else {
        setImportError('');
        setShowImportModal(false);
        toast({
          title: t.common.success,
          description: t.admin.importSuccess.replace('{count}', String(result.success)),
        });
      }
      fetchUsers();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to import users';
      setImportError(message);
    } finally {
      setSubmitting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle export
  const handleExport = () => {
    window.open(exportUsersUrl(), '_blank');
  };

  // Handle add group
  const handleAddGroup = async () => {
    if (!newGroupName.trim()) return;

    try {
      setSubmitting(true);
      await createGroup({ name: newGroupName.trim() });
      setNewGroupName('');
      toast({
        title: t.common.success,
        description: t.admin.groupCreated,
      });
      fetchGroups();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create group';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete group
  const handleDeleteGroup = async (id: string, name: string) => {
    if (!confirm(t.admin.deleteGroupConfirm.replace('{name}', name))) {
      return;
    }

    try {
      setSubmitting(true);
      await deleteGroup(id);
      toast({
        title: t.common.success,
        description: t.admin.groupDeleted,
      });
      fetchGroups();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete group';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle group selection in form
  const toggleGroup = (groupName: string) => {
    setFormData((prev) => ({
      ...prev,
      groups: prev.groups.includes(groupName)
        ? prev.groups.filter((g) => g !== groupName)
        : [...prev.groups, groupName],
    }));
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingUser(null);
    setFormData({ email: '', name: '', password: '', role: 'user', groups: [] });
  };

  const openEditModal = (user: UserData) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      password: '',
      role: user.role,
      groups: user.groups,
    });
    setShowAddModal(true);
  };

  const roleOptions = [
    { value: 'all', label: t.admin.allRoles },
    { value: 'admin', label: t.roles.admin },
    { value: 'creator', label: t.roles.creator },
    { value: 'user', label: t.roles.user },
  ];

  const groupOptions = [
    { value: 'all', label: t.admin.allGroups },
    ...groups.map((g) => ({ value: g.name, label: g.name })),
  ];

  const totalPages = Math.ceil(total / pageSize);

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t.admin.userManagement}</h1>
          <p className="text-gray-500 mt-1">{t.admin.userManagementDesc}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => setShowGroupModal(true)}>
            <Filter className="w-4 h-4 mr-2" />
            {t.admin.manageGroups}
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            {t.admin.export}
          </Button>
          <Button variant="outline" onClick={() => setShowImportModal(true)}>
            <Upload className="w-4 h-4 mr-2" />
            {t.admin.bulkImport}
          </Button>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t.admin.addUser}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px] max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder={t.admin.searchUsers}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Select
              options={roleOptions}
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-36"
            />
            <Select
              options={groupOptions}
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              className="w-36"
            />
            {selectedIds.size > 0 && (
              <Button variant="danger" size="sm" onClick={handleBulkDelete} disabled={submitting}>
                <Trash2 className="w-4 h-4 mr-2" />
                {t.admin.deleteSelected} ({selectedIds.size})
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Users table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <input
                  type="checkbox"
                  checked={selectedIds.size === users.length && users.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300"
                />
              </TableHead>
              <TableHead>{t.admin.userInfo}</TableHead>
              <TableHead>{t.common.role}</TableHead>
              <TableHead>{t.common.groups}</TableHead>
              <TableHead>{t.common.status}</TableHead>
              <TableHead className="w-24">{t.common.actions}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>{t.admin.noUsers}</p>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(user.id)}
                      onChange={() => handleSelect(user.id)}
                      className="rounded border-gray-300"
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-primary-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-primary-700">
                          {user.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        user.role === 'admin'
                          ? 'danger'
                          : user.role === 'creator'
                          ? 'info'
                          : 'default'
                      }
                    >
                      {t.roles[user.role as keyof typeof t.roles]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {user.groups.slice(0, 3).map((group) => (
                        <Badge key={group} variant="default">
                          {group}
                        </Badge>
                      ))}
                      {user.groups.length > 3 && (
                        <Badge variant="default">+{user.groups.length - 3}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.isActive ? 'success' : 'warning'}>
                      {user.isActive ? t.common.active : t.common.inactive}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEditModal(user)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                        disabled={submitting}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        disabled={submitting}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-sm text-gray-500">
              {t.admin.showingUsers
                .replace('{from}', String((page - 1) * pageSize + 1))
                .replace('{to}', String(Math.min(page * pageSize, total)))
                .replace('{total}', String(total))}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-700">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Add/Edit User Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={closeModal}
        title={editingUser ? t.admin.editUser : t.admin.addUserTitle}
        description={editingUser ? t.admin.editUserDesc : t.admin.addUserDesc}
      >
        <div className="space-y-4">
          <Input
            label={t.common.email}
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="user@company.com"
            disabled={!!editingUser}
          />
          <Input
            label={t.common.name}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder={t.common.name}
          />
          <Input
            label={editingUser ? t.admin.newPassword : t.common.password}
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            placeholder={editingUser ? t.admin.leaveBlankToKeep : t.common.password}
          />
          <Select
            label={t.common.role}
            options={[
              { value: 'user', label: t.admin.roleUser },
              { value: 'creator', label: t.admin.roleCreator },
              { value: 'admin', label: t.admin.roleAdmin },
            ]}
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t.common.groups}</label>
            <div className="flex flex-wrap gap-2 p-3 border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
              {groups.map((group) => (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => toggleGroup(group.name)}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                    formData.groups.includes(group.name)
                      ? 'bg-primary-100 text-primary-700 border border-primary-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {formData.groups.includes(group.name) && <Check className="w-3 h-3 inline mr-1" />}
                  {group.name}
                </button>
              ))}
              {groups.length === 0 && (
                <p className="text-sm text-gray-500">{t.admin.noGroups}</p>
              )}
            </div>
          </div>
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={closeModal} disabled={submitting}>
            {t.common.cancel}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {editingUser ? t.common.save : t.common.add}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Import Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => {
          setShowImportModal(false);
          setImportError('');
        }}
        title={t.admin.bulkImportTitle}
        description={t.admin.bulkImportDesc}
        size="lg"
      >
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">{t.admin.csvFormat}</h4>
            <p className="text-sm text-blue-700 mb-3">
              {t.admin.csvFormatDesc}
            </p>
            <code className="block p-3 bg-white rounded text-sm text-gray-700 whitespace-pre-line">
              {t.admin.csvExample}
            </code>
            <p className="text-xs text-blue-600 mt-2 whitespace-pre-line">
              {t.admin.csvNote}
            </p>
          </div>

          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-400 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            {submitting ? (
              <Loader2 className="w-10 h-10 text-primary-400 mx-auto mb-3 animate-spin" />
            ) : (
              <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
            )}
            <p className="text-gray-600 mb-1">{t.admin.uploadCSV}</p>
            <p className="text-sm text-gray-400">{t.admin.csvSupport}</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
              disabled={submitting}
            />
          </div>

          {importError && (
            <div className="p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-red-700 whitespace-pre-line">{importError}</p>
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowImportModal(false)}>
              {t.common.close}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Group Management Modal */}
      <Modal
        isOpen={showGroupModal}
        onClose={() => setShowGroupModal(false)}
        title={t.admin.manageGroupsTitle}
        description={t.admin.manageGroupsDesc}
      >
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder={t.admin.newGroupName}
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddGroup()}
              disabled={submitting}
            />
            <Button onClick={handleAddGroup} disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t.common.add}
            </Button>
          </div>

          <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
            {groups.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                {t.admin.noGroups}
              </div>
            ) : (
              groups.map((group) => (
                <div key={group.id} className="flex items-center justify-between p-3">
                  <div>
                    <p className="font-medium text-gray-900">{group.name}</p>
                    <p className="text-sm text-gray-500">
                      {group.memberCount} {t.admin.members}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteGroup(group.id, group.name)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    disabled={submitting}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowGroupModal(false)}>
            {t.common.close}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
